import { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { flags } from 'services/api';
import {
  Flags,
  Flag,
  FlagsFromJSON,
  FlagsApi,
  ListFlagsRequest,
  ListFlagsSortEnum,
  CreateFlagRequest,
  FlagMap,
} from 'types/backend';
import { PageableAPIResponse } from 'types/frontend';
import {
  EntityListPermissions,
  EntityListPermissionsConfig,
  getEntityListPermissions,
} from 'services/api/permissions/EntityListPermissions';
import paginateApiResponse from 'services/api/utils/paginateApiResponse';
import combineFetchParams from './utils/combineFetchParams';

import GranularPermissions, {
  EntityType,
  useGranularPermissions,
} from 'services/api/permissions/GranularPermissions';
/**
 * Type Definitions
 *
 */

export interface FlagsDataProps {
  archiveFlags: (keys: string[]) => Promise<FlagMap>;
  canCreateFlag: boolean;
  createFlag: (args: CreateFlagRequest) => Promise<Flag>;
  deleteFlag: (flagKey: string) => void;
  error?: Error | unknown;
  fetchFlagParams: ListFlagsRequest;
  filterCount: number;
  flagsData: PageableAPIResponse<Flags>;
  getCanDeleteFlag: (flagKey: string) => boolean;
  getCanManagePermissions: (flagKey: string) => boolean;
  isFetchingFlags: boolean;
  isLoadingFlags: boolean;
  managePermissions: ({
    flagId,
    flagKey,
  }: {
    flagId: number;
    flagKey: string;
  }) => void;
  searchForFlags: (params: Partial<ListFlagsRequest>) => Promise<Flags>;
  unarchiveFlags: (keys: string[]) => Promise<FlagMap>;
  updateFetchFlagParams: (args?: Partial<ListFlagsRequest>) => void;
}

export type FlagsRouteParams = {
  project_id: string;
};

export interface FlagsPermissions extends EntityListPermissions {}

export const DEFAULT_FLAGS_SORT = [{ desc: true, id: 'created_time' }];

/**
 * Permissions
 *
 */

export const getFlagsPermissions = (
  config: EntityListPermissionsConfig<Flags>
): FlagsPermissions => getEntityListPermissions<Flags>(config);

/**
 * Flags Services
 *
 * @description Each service will perform any steps that are required before a
 * request is made. For example, handling permissions or data-sanitization.
 *
 */

export const getFlagsQueryService = ({
  flagsApi,
  params,
}: {
  flagsApi: FlagsApi;
  params: FlagsRouteParams;
}) => ({
  fetch: async (
    queryKey: string,
    requestParams: Partial<ListFlagsRequest>
  ): Promise<Flags> => {
    const { project_id } = params;
    try {
      const params = {
        // arbitrary large number needed to fetch prev_url and next_url arrays accurately
        page_window: 1000,
        per_page: 50,
        project_id: Number(project_id),
        sort: [ListFlagsSortEnum.UpdatedTimedesc],
        ...requestParams,
      };

      if (requestParams.query?.length === 0) {
        delete params.query;
      }
      return await flagsApi.listFlags(params);
    } catch (error) {
      throw new Error(
        JSON.stringify({
          message: `Fetching flags for project ${project_id} failed.`,
          ...error,
        })
      );
    }
  },
});

export const getFlagsMutationService = ({
  flagsApi,
  params,
  permissions,
}: {
  flagsApi: FlagsApi;
  params: FlagsRouteParams;
  permissions: FlagsPermissions;
}) => ({
  archive: async (keys: string[]): Promise<FlagMap> => {
    const { project_id } = params;
    const flagsToArchiveString = keys.join(' ,');
    if (!keys.some(key => permissions.getCanArchive(key))) {
      throw new Error(
        `User does not have permission to archive ${flagsToArchiveString}`
      );
    }
    try {
      return await flagsApi.archiveFlags({
        bulk_operations_request_body: {
          keys: keys,
        },
        project_id: Number(project_id),
      });
    } catch (error) {
      throw new Error(
        JSON.stringify({
          message: `Archiving flag${
            keys.length > 1 ? 's' : ''
          } ${flagsToArchiveString} failed.`,
          ...error,
        })
      );
    }
  },
  create: async (requestParams: CreateFlagRequest): Promise<Flag> => {
    if (!permissions.getCanCreate()) {
      throw new Error('User does not have permission to create.');
    }
    try {
      return await flagsApi.createFlag(requestParams);
    } catch (error) {
      throw new Error(
        JSON.stringify({
          message: `Creating a flag in project ${requestParams.project_id} failed.`,
          ...error,
        })
      );
    }
  },
  delete: async (flagKey: string): Promise<void> => {
    if (!permissions.getCanDeleteItem(flagKey)) {
      throw new Error(`User does not have permission to delete ${flagKey}.`);
    }
    const { project_id } = params;
    try {
      return await flagsApi.deleteFlag({
        flag_key: flagKey,
        project_id: Number(project_id),
      });
    } catch (error) {
      throw new Error(
        JSON.stringify({
          message: `Deleting flag ${flagKey} failed.`,
          ...error,
        })
      );
    }
  },
  managePermissions: async ({
    flagId,
    flagKey,
  }: {
    flagId: number;
    flagKey: string;
  }): Promise<void> => {
    const { project_id } = params;

    GranularPermissions.openManagePermissions(
      Number(project_id),
      EntityType.FLAG,
      flagId,
      flagKey
    );
  },
  unarchive: async (keys: string[]): Promise<FlagMap> => {
    const { project_id } = params;
    const flagsToUnarchiveString = keys.join(' ,');
    if (!keys.some(key => permissions.getCanUnarchive(key))) {
      throw new Error(
        `User does not have permission to unarchive ${flagsToUnarchiveString}.`
      );
    }
    try {
      return await flagsApi.unarchiveFlags({
        bulk_operations_request_body: {
          keys: keys,
        },
        project_id: Number(project_id),
      });
    } catch (error) {
      throw new Error(
        JSON.stringify({
          message: `Unarchiving flag${
            keys.length > 1 ? 's' : ''
          } ${flagsToUnarchiveString} failed.`,
          ...error,
        })
      );
    }
  },
});

/**
 * An HOC responsible for fetching Flags from the backend API and
 * passing them down to the UI component.
 *
 * Typescript support for HOCs like this can be found here:
 *   https://github.com/typescript-cheatsheets/react-typescript-cheatsheet/blob/master/HOC.md#section-0-full-hoc-example
 */
function useFlagsData({
  initialFilter,
  projectId,
}: {
  initialFilter: { [key: string]: any };
  projectId?: string;
}): FlagsDataProps {
  const { project_id: routeProjectId } = useParams();
  const project_id = projectId ?? routeProjectId;
  const QUERY_KEY: string[] = [`flags`, String(project_id)];
  const queryClient = useQueryClient();

  const BASE_PARAMS = useMemo(() => {
    return {
      project_id: Number(project_id),
      sort: [ListFlagsSortEnum.CreatedTimedesc],
    };
  }, [project_id]);

  const INITIAL_PARAMS = useMemo(() => {
    return {
      ...BASE_PARAMS,
      ...initialFilter,
    };
  }, [BASE_PARAMS, initialFilter]);

  const [fetchParams, setFetchParams] = useState<ListFlagsRequest>(
    INITIAL_PARAMS
  );

  const queryService = useMemo(
    () => getFlagsQueryService({ flagsApi: flags, params: { project_id } }),
    [project_id]
  );

  const {
    isFetching, // Subsequent fetch
    isLoading, // First fetch
    data: flagDataToReturn,
    error,
  } = useQuery({
    keepPreviousData: true,
    queryFn: () => queryService.fetch(QUERY_KEY, fetchParams),
    queryKey: [...QUERY_KEY, fetchParams],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const updateFetchFlagParams = useCallback(
    (params?: Partial<ListFlagsRequest>) =>
      setFetchParams(combineFetchParams(INITIAL_PARAMS, params)),
    [INITIAL_PARAMS]
  );

  /**
   * This function is only applicable for use in
   * OUI's SearchPicker, which requires a function
   * that returns a Promise with the resolved data
   */
  const searchForFlags = async (params: Partial<ListFlagsRequest>) => {
    const result = await queryService.fetch(QUERY_KEY, {
      ...fetchParams,
      ...params,
    });
    return result;
  };

  const isGranularPermissionsEnabled = useGranularPermissions();
  const permissions = useMemo(
    () =>
      getFlagsPermissions({
        isGranularPermissionsEnabled,
        latestApiResponse: flagDataToReturn as Flags | undefined,
        resolvedApiResponse: flagDataToReturn as Flags | undefined,
      }),
    [isGranularPermissionsEnabled, flagDataToReturn]
  );

  const mutationService = useMemo(
    () =>
      getFlagsMutationService({
        flagsApi: flags,
        params: { project_id },
        permissions,
      }),
    [permissions, project_id]
  );

  const archiveMutation = useMutation({
    mutationFn: mutationService.archive,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const createFlagMutation = useMutation({
    mutationFn: mutationService.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const deleteFlagMutation = useMutation({
    mutationFn: mutationService.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const managePermissionsMutation = useMutation({
    mutationFn: mutationService.managePermissions,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: mutationService.unarchive,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  return {
    archiveFlags: archiveMutation.mutateAsync,
    canCreateFlag: permissions.getCanCreate(),
    createFlag: createFlagMutation.mutateAsync,
    deleteFlag: deleteFlagMutation.mutateAsync,
    error,
    fetchFlagParams: fetchParams,
    flagsData: paginateApiResponse(
      flagDataToReturn || FlagsFromJSON({ items: [] }),
      updateFetchFlagParams
    ),
    getCanDeleteFlag: permissions.getCanDeleteItem,
    getCanManagePermissions: permissions.getCanManagePermissions,
    isFetchingFlags: isFetching,
    isLoadingFlags: isLoading,
    managePermissions: managePermissionsMutation.mutateAsync,
    searchForFlags,
    unarchiveFlags: unarchiveMutation.mutateAsync,
    updateFetchFlagParams,
  };
}

export default useFlagsData;
